/** Tipos del módulo de Comisiones */

export type CommissionRuleType = 'percentage' | 'fixed_per_unit' | 'fixed_per_line' | 'tiered' | 'goal';
export type CommissionScope = 'all' | 'service' | 'product' | 'category' | 'employee' | 'branch';
export type CommissionBase = 'line_total' | 'line_total_pre_discount' | 'unit_price' | 'quantity' | 'custom';
export type AttributionRole = 'cashier' | 'sold_by' | 'performed_by' | 'split';
export type EarningSourceType = 'sale_item' | 'work_order_task' | 'appointment' | 'manual';
export type EarningStatus = 'pending' | 'settled' | 'voided';
export type SettlementStatus = 'draft' | 'approved' | 'paid' | 'voided';
export type AdjustmentType = 'advance' | 'deduction' | 'product_withdrawal' | 'bonus' | 'correction';
export type AdjustmentStatus = 'pending' | 'settled' | 'voided';
export type PaymentMethod = 'cash' | 'expense' | 'bank_transfer' | 'other';

export type CommissionPlan = {
  id: string;
  name: string;
  description: string | null;
  branchId: string | null;
  isActive: boolean;
  isDefault: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  ruleCount: number;
  assignmentCount: number;
};

export type CommissionRule = {
  id: string;
  ruleType: CommissionRuleType;
  scope: CommissionScope;
  scopeRef: string | null;
  ratePercent: number | null;
  fixedAmount: number | null;
  commissionBase: CommissionBase;
  tiers: TierConfig[];
  attributionRole: AttributionRole;
  splitPercent: number;
  priority: number;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  dayOfWeek: number | null;
  branchId: string | null;
};

export type TierConfig = {
  min_amount?: number;
  min_qty?: number;
  rate_percent: number;
};

export type CommissionAssignment = {
  id: string;
  planId: string;
  planName: string;
  employeeUserId: string;
  employeeName: string;
  isActive: boolean;
  assignedAt: string;
};

export type CommissionEarning = {
  id: string;
  employeeUserId: string;
  employeeName: string;
  sourceType: EarningSourceType;
  sourceId: string;
  sourceRef: string | null;
  commissionableAmount: number;
  commissionAmount: number;
  ratePercent: number | null;
  ruleType: string | null;
  description: string | null;
  earnedAt: string;
  status: EarningStatus;
  settlementId: string | null;
};

export type CommissionSummary = {
  employeeUserId: string;
  employeeName: string;
  earningsCount: number;
  grossEarnings: number;
  pendingEarnings: number;
  settledEarnings: number;
  adjustmentsTotal: number;
  netPayable: number;
};

export type MyCommissionSummary = {
  earningsCount: number;
  grossEarnings: number;
  pendingEarnings: number;
  settledEarnings: number;
  adjustmentsTotal: number;
  netPayable: number;
};

export type CommissionAdjustment = {
  id: string;
  employeeUserId: string;
  employeeName: string;
  adjustmentType: AdjustmentType;
  amount: number;
  productId: string | null;
  quantity: number | null;
  unitValue: number | null;
  reason: string;
  description: string | null;
  effectiveDate: string;
  status: AdjustmentStatus;
  settlementId: string | null;
  createdBy: string | null;
  createdAt: string;
};

export type CommissionSettlement = {
  id: string;
  employeeUserId: string;
  employeeName: string;
  branchId: string | null;
  periodFrom: string;
  periodTo: string;
  grossEarnings: number;
  adjustmentsTotal: number;
  netPayable: number;
  status: SettlementStatus;
  approvedAt: string | null;
  paidAt: string | null;
  paymentMethod: PaymentMethod | null;
  paymentRef: string | null;
  note: string | null;
  createdAt: string;
};

export type SettlementDetail = {
  ok: boolean;
  settlement: CommissionSettlement & {
    organizationId: string;
    voidedAt: string | null;
    voidedBy: string | null;
    voidReason: string | null;
    approvedBy: string | null;
    paidBy: string | null;
    updatedAt: string;
  };
  earnings: Array<{
    id: string;
    source_type: string;
    source_id: string;
    source_ref: string | null;
    commissionable_amount: number;
    commission_amount: number;
    rate_percent: number | null;
    description: string | null;
    earned_at: string;
  }>;
  adjustments: Array<{
    id: string;
    adjustment_type: string;
    amount: number;
    reason: string;
    description: string | null;
    effective_date: string;
  }>;
};

export const RULE_TYPE_LABELS: Record<CommissionRuleType, string> = {
  percentage: 'Porcentaje',
  fixed_per_unit: 'Fijo por unidad',
  fixed_per_line: 'Fijo por línea',
  tiered: 'Escalonado',
  goal: 'Por meta',
};

/**
 * Título legible de una comisión generada. El RPC la persiste como
 * 'Comisión <rule_type> · <ítem>' con el enum en crudo.
 */
export function commissionEarningTitle(description: string | null | undefined): string {
  const raw = description?.trim();
  if (!raw) return 'Comisión';
  const m = /^Comisión (\w+)(?:\s*·\s*(.+))?$/.exec(raw);
  if (!m) return raw;
  const label = RULE_TYPE_LABELS[m[1] as CommissionRuleType] ?? m[1];
  return m[2] ?? `Comisión ${label.toLowerCase()}`;
}

/**
 * El RPC marca las ventas sin plan asignado con rule_type 'no_plan'
 * (fallback: description 'Sin plan de comisión asignado'). La UI consolida
 * estas filas en un banner único en vez de repetir el texto por fila.
 */
export function isNoPlanEarning(
  e: Pick<CommissionEarning, 'ruleType' | 'commissionAmount' | 'description'>,
): boolean {
  if (e.ruleType === 'no_plan') return true;
  return e.commissionAmount === 0 && (e.description ?? '').toLowerCase().includes('sin plan');
}

/**
 * KPIs con el mismo valor no informan nada nuevo — queda el último de la
 * serie (el más accionable: «A liquidar» / «Neto a cobrar»).
 */
export function dedupeKpiValues<T extends { value: string }>(items: T[]): T[] {
  return items.filter(
    (item, i) => !items.slice(i + 1).some((later) => later.value === item.value),
  );
}

export const SCOPE_LABELS: Record<CommissionScope, string> = {
  all: 'Todo',
  service: 'Servicio específico',
  product: 'Producto específico',
  category: 'Categoría',
  employee: 'Empleado específico',
  branch: 'Sucursal específica',
};

export const COMMISSION_BASE_LABELS: Record<CommissionBase, string> = {
  line_total: 'Total de línea',
  line_total_pre_discount: 'Total antes de descuento',
  unit_price: 'Precio unitario',
  quantity: 'Cantidad',
  custom: 'Personalizado',
};

export const ATTRIBUTION_ROLE_LABELS: Record<AttributionRole, string> = {
  cashier: 'Cajero',
  sold_by: 'Vendedor',
  performed_by: 'Quien realizó',
  split: 'Dividido',
};

export const ADJUSTMENT_TYPE_LABELS: Record<AdjustmentType, string> = {
  advance: 'Adelanto',
  deduction: 'Descuento',
  product_withdrawal: 'Retiro de producto',
  bonus: 'Bonus',
  correction: 'Corrección',
};

export const SETTLEMENT_STATUS_LABELS: Record<SettlementStatus, string> = {
  draft: 'Borrador',
  approved: 'Aprobada',
  paid: 'Pagada',
  voided: 'Anulada',
};

export const SETTLEMENT_STATUS_COLORS: Record<SettlementStatus, string> = {
  draft: '#6b7280',
  approved: '#2563eb',
  paid: '#16a34a',
  voided: '#dc2626',
};

export const EARNING_STATUS_LABELS: Record<EarningStatus, string> = {
  pending: 'Pendiente',
  settled: 'Liquidada',
  voided: 'Anulada',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Efectivo (caja)',
  expense: 'Gasto',
  bank_transfer: 'Transferencia',
  other: 'Otro',
};
